'use client'

import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Search, Loader2, Grid3x3 } from 'lucide-react'
import { useSynopticProject } from '@/lib/synoptic-project-provider'
import { ProjectCard } from './project-card'
import { RenameProjectDialog } from './rename-project-dialog'
import { toast } from 'sonner'
import { useLanguage } from '@/lib/i18n/language-provider'

interface ProjectPickerProps {
  open: boolean
  onClose: () => void
  onSelectProject: (projectId: string) => void
}

export function ProjectPicker({
  open,
  onClose,
  onSelectProject
}: ProjectPickerProps) {
  const {
    projects,
    loadProjects,
    createProject,
    duplicateProject,
    deleteProject,
    updateProjectName
  } = useSynopticProject()
  const { t } = useLanguage()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // State pour le dialog de renommage
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean
    projectId: string
    currentName: string
  }>({
    open: false,
    projectId: '',
    currentName: ''
  })
  
  // Utiliser useRef pour éviter les boucles infinies dues aux changements de loadProjects
  const loadProjectsRef = useRef(loadProjects)
  useEffect(() => {
    loadProjectsRef.current = loadProjects
  }, [loadProjects])
  
  // Charger les projets à l'ouverture
  useEffect(() => {
    if (open) {
      setIsLoading(true)
      loadProjectsRef.current().finally(() => setIsLoading(false))
    }
  }, [open])
  
  // Filtrer les projets par nom
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )
  
  // Créer un nouveau projet
  const handleCreateProject = async () => {
    setIsCreating(true)
    try {
      const project = await createProject()
      if (project) {
        onSelectProject(project.id)
        onClose()
      }
    } finally {
      setIsCreating(false)
    }
  }
  
  // Renommer un projet
  const handleRename = (projectId: string, currentName: string) => {
    setRenameDialog({
      open: true,
      projectId,
      currentName
    })
  }
  
  const handleRenameSubmit = async (newName: string) => {
    // Passer le projectId pour renommer le bon projet (pas forcément le projet courant)
    await updateProjectName(newName, renameDialog.projectId)
    setRenameDialog({ open: false, projectId: '', currentName: '' })
  }
  
  // Dupliquer un projet
  const handleDuplicate = async (projectId: string, projectName: string) => {
    try {
      const copyText = t("synoptic.card.copy")
      await duplicateProject(projectId, `${projectName} (${copyText})`)
      // Realtime met à jour la liste automatiquement
    } catch (error) {
      // Error handled in provider
    }
  }
  
  // Supprimer un projet
  const handleDelete = async (projectId: string, projectName: string) => {
    const confirmMessage = t("synoptic.projects.deleteConfirm", { name: projectName })
    const confirmed = window.confirm(confirmMessage)
    
    if (!confirmed) return
    
    await deleteProject(projectId)
    // Realtime met à jour la liste automatiquement
  }
  
  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t("synoptic.projects.title")}</DialogTitle>
            <DialogDescription>
              {t("synoptic.projects.description")}
            </DialogDescription>
          </DialogHeader>
          
          {/* Search + Create */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t("synoptic.projects.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              onClick={handleCreateProject}
              disabled={isCreating}
              className="gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("synoptic.projects.creating")}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t("synoptic.projects.newProject")}
                </>
              )}
            </Button>
          </div>
          
          {/* Projects Grid */}
          <div className="flex-1 overflow-y-auto pr-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProjects.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onOpen={(id) => {
                      onSelectProject(id)
                      onClose()
                    }}
                    onRename={handleRename}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Grid3x3 className="h-16 w-16 text-muted-foreground mb-4 opacity-50" />
                <p className="text-lg font-medium text-muted-foreground mb-2">
                  {searchQuery ? t("synoptic.projects.noProjectsFound") : t("synoptic.projects.noProjects")}
                </p>
                <p className="text-sm text-muted-foreground max-w-md">
                  {searchQuery
                    ? t("synoptic.projects.tryAnotherSearch")
                    : t("synoptic.projects.createFirstSynoptic")}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={handleCreateProject}
                    disabled={isCreating}
                    className="mt-6 gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t("synoptic.projects.createFirst")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de renommage */}
      <RenameProjectDialog
        open={renameDialog.open}
        currentName={renameDialog.currentName}
        onClose={() => setRenameDialog({ open: false, projectId: '', currentName: '' })}
        onSubmit={handleRenameSubmit}
      />
    </>
  )
}

